import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .ft-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    color: #e2e8f0;
    overflow-x: hidden;
  }

  /* ── hero ── */
  .ft-hero {
    text-align: center;
    padding: 80px 24px 64px;
    position: relative;
  }
  .ft-hero-bg {
    position: absolute; inset: 0; pointer-events: none; overflow: hidden; z-index: 0;
  }
  .ft-hero-blob {
    position: absolute; border-radius: 50%; filter: blur(90px);
  }
  .ft-hero-blob-1 { width: 600px; height: 600px; background: rgba(99,102,241,0.1); top: -200px; left: -200px; }
  .ft-hero-blob-2 { width: 450px; height: 450px; background: rgba(79,70,229,0.07); top: -100px; right: -150px; }

  .ft-hero-inner { position: relative; z-index: 1; max-width: 720px; margin: 0 auto; }

  .ft-eyebrow {
    display: inline-block;
    font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em;
    color: #818cf8; background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);
    border-radius: 100px; padding: 5px 14px; margin-bottom: 20px;
  }

  .ft-h1 {
    font-size: 3rem; font-weight: 900; color: #f1f5f9;
    letter-spacing: -0.04em; line-height: 1.1; margin: 0 0 16px;
  }
  .ft-h1 span {
    background: linear-gradient(135deg, #818cf8, #6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }

  .ft-hero-sub {
    font-size: 1.05rem; color: #64748b; line-height: 1.7;
    max-width: 560px; margin: 0 auto 32px;
  }

  .ft-hero-cta {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white; text-decoration: none;
    padding: 13px 28px; border-radius: 12px;
    font-size: 15px; font-weight: 700;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35);
    transition: all 0.2s;
  }
  .ft-hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(99,102,241,0.5); }

  /* ── section wrapper ── */
  .ft-section { max-width: 1200px; margin: 0 auto; padding: 0 24px 80px; }
  .ft-section-title {
    font-size: 1.5rem; font-weight: 900; color: #f1f5f9;
    letter-spacing: -0.03em; margin: 0 0 8px;
  }
  .ft-section-sub { font-size: 14px; color: #475569; margin: 0 0 36px; }

  /* ── plan tabs ── */
  .ft-tabs {
    display: inline-flex; align-items: center;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.15);
    border-radius: 12px; padding: 4px; gap: 4px;
    margin-bottom: 40px;
  }
  .ft-tab {
    padding: 8px 20px; border-radius: 9px;
    font-size: 13px; font-weight: 700;
    cursor: pointer; border: none; font-family: inherit;
    transition: all 0.2s;
    background: none; color: #475569;
  }
  .ft-tab:hover { color: #94a3b8; }
  .ft-tab-active { background: rgba(99,102,241,0.2); color: #a5b4fc; }

  /* ── feature cards grid ── */
  .ft-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
    margin-bottom: 80px;
  }

  .ft-card {
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 20px;
    padding: 28px;
    transition: border-color 0.25s, transform 0.25s;
  }
  .ft-card:hover { border-color: rgba(99,102,241,0.28); transform: translateY(-3px); }

  .ft-card-icon-wrap {
    display: inline-flex; align-items: center; justify-content: center;
    width: 48px; height: 48px;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 14px; font-size: 22px;
    margin-bottom: 16px;
  }

  .ft-card-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 8px; }

  .ft-card-title { font-size: 1.05rem; font-weight: 800; color: #f1f5f9; margin: 0; }
  .ft-card-desc  { font-size: 14px; color: #64748b; line-height: 1.65; margin: 0 0 18px; }

  /* plan availability pills */
  .ft-plan-pills { display: flex; gap: 6px; flex-wrap: wrap; }
  .ft-pill {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
    padding: 3px 9px; border-radius: 100px;
  }
  .ft-pill-free     { background: rgba(100,116,139,0.12); color: #94a3b8; border: 1px solid rgba(100,116,139,0.18); }
  .ft-pill-pro      { background: rgba(99,102,241,0.12);  color: #a5b4fc; border: 1px solid rgba(99,102,241,0.22); }
  .ft-pill-business { background: rgba(251,191,36,0.1);   color: #fbbf24; border: 1px solid rgba(251,191,36,0.2); }

  /* ── comparison table ── */
  .ft-table-wrap { overflow-x: auto; margin-bottom: 80px; }

  table.ft-table {
    width: 100%; border-collapse: collapse; font-size: 14px;
    min-width: 600px;
  }

  .ft-table thead th {
    padding: 14px 20px; text-align: left;
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
    color: #475569;
    border-bottom: 1px solid rgba(99,102,241,0.15);
  }
  .ft-table thead th:first-child { width: 40%; }

  .ft-table td {
    padding: 14px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    color: #94a3b8;
    vertical-align: middle;
  }
  .ft-table tbody tr:hover td { background: rgba(99,102,241,0.04); }
  .ft-table tbody tr:last-child td { border-bottom: none; }

  .ft-table .ft-check { color: #22c55e; font-size: 16px; }
  .ft-table .ft-cross { color: #1e293b; font-size: 16px; }
  .ft-table .ft-val   { color: #f1f5f9; font-weight: 700; }

  /* table plan header highlight */
  .ft-col-pro      { color: #a5b4fc !important; }
  .ft-col-business { color: #fbbf24 !important; }

  /* category row */
  .ft-cat-row td {
    background: rgba(99,102,241,0.04);
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.09em;
    color: #475569; padding: 10px 20px;
    border-bottom: 1px solid rgba(99,102,241,0.1);
  }

  /* ── CTA strip ── */
  .ft-cta-strip {
    background: rgba(99,102,241,0.07);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 24px; padding: 48px 40px;
    text-align: center;
    margin: 0 24px 80px;
    max-width: 1200px;
    margin-left: auto; margin-right: auto;
  }
  .ft-cta-title { font-size: 1.75rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.03em; margin: 0 0 12px; }
  .ft-cta-sub   { font-size: 15px; color: #64748b; margin: 0 0 28px; }
  .ft-cta-btns  { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

  .ft-cta-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, #6366f1, #4f46e5); color: white;
    text-decoration: none; padding: 13px 28px; border-radius: 12px;
    font-size: 15px; font-weight: 700;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35); transition: all 0.2s;
  }
  .ft-cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(99,102,241,0.5); }

  .ft-cta-secondary {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.05); color: #94a3b8;
    text-decoration: none; padding: 13px 28px; border-radius: 12px;
    font-size: 15px; font-weight: 700;
    border: 1px solid rgba(99,102,241,0.18); transition: all 0.2s;
  }
  .ft-cta-secondary:hover { background: rgba(99,102,241,0.12); color: #a5b4fc; }
  /* ── brand showcase ── */
  .ft-brand-showcase {
    max-width: 1200px; margin: 0 auto; padding: 0 24px 80px;
  }
  .ft-brand-box {
    background: linear-gradient(135deg, rgba(251,191,36,0.06) 0%, rgba(245,158,11,0.04) 100%);
    border: 1px solid rgba(251,191,36,0.22);
    border-radius: 28px;
    padding: 48px 52px;
    position: relative;
    overflow: hidden;
  }
  .ft-brand-box::before {
    content: '';
    position: absolute; top: -120px; right: -120px;
    width: 380px; height: 380px;
    background: radial-gradient(circle, rgba(251,191,36,0.12) 0%, transparent 70%);
    pointer-events: none;
  }
  .ft-brand-top {
    display: flex; align-items: flex-start; gap: 20px; margin-bottom: 36px;
  }
  .ft-brand-icon {
    flex-shrink: 0;
    width: 60px; height: 60px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(251,191,36,0.12);
    border: 1px solid rgba(251,191,36,0.28);
    border-radius: 18px; font-size: 28px;
  }
  .ft-brand-badge {
    display: inline-block;
    font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em;
    color: #fbbf24; background: rgba(251,191,36,0.1); border: 1px solid rgba(251,191,36,0.22);
    border-radius: 100px; padding: 4px 12px; margin-bottom: 10px;
  }
  .ft-brand-title {
    font-size: 1.7rem; font-weight: 900; color: #f1f5f9;
    letter-spacing: -0.035em; margin: 0 0 10px;
  }
  .ft-brand-desc {
    font-size: 15px; color: #64748b; line-height: 1.7; margin: 0;
    max-width: 620px;
  }
  .ft-brand-modes {
    display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 32px;
  }
  @media (max-width: 680px) { .ft-brand-modes { grid-template-columns: 1fr; } }
  .ft-brand-mode {
    background: rgba(0,0,0,0.25);
    border: 1px solid rgba(251,191,36,0.15);
    border-radius: 18px; padding: 24px 26px;
  }
  .ft-brand-mode-label {
    font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em;
    color: #fbbf24; margin: 0 0 6px;
  }
  .ft-brand-mode-name {
    font-size: 1rem; font-weight: 800; color: #f1f5f9; margin: 0 0 10px;
  }
  .ft-brand-mode-desc {
    font-size: 13px; color: #64748b; line-height: 1.6; margin: 0 0 18px;
  }
  .ft-brand-url-demo {
    background: rgba(0,0,0,0.35);
    border: 1px solid rgba(251,191,36,0.2);
    border-radius: 10px; padding: 12px 16px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px; line-height: 1.5;
  }
  .ft-url-dim  { color: #475569; }
  .ft-url-brand { color: #fbbf24; font-weight: 800; }
  .ft-url-code  { color: #a5b4fc; font-weight: 700; }
  .ft-brand-steps {
    background: rgba(0,0,0,0.2);
    border: 1px solid rgba(99,102,241,0.15);
    border-radius: 18px; padding: 24px 28px;
  }
  .ft-brand-steps-title {
    font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em;
    color: #818cf8; margin: 0 0 16px;
  }
  .ft-brand-steps-list {
    list-style: none; padding: 0; margin: 0;
    display: flex; flex-direction: column; gap: 12px;
  }
  .ft-brand-step {
    display: flex; align-items: flex-start; gap: 14px;
  }
  .ft-brand-step-num {
    flex-shrink: 0;
    width: 26px; height: 26px;
    background: rgba(99,102,241,0.15); border: 1px solid rgba(99,102,241,0.25);
    border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 800; color: #818cf8;
  }
  .ft-brand-step-text {
    font-size: 14px; color: #94a3b8; line-height: 1.55; padding-top: 3px;
  }
  .ft-brand-step-text strong { color: #e2e8f0; }

  @media (max-width: 640px) {
    .ft-h1 { font-size: 2rem; }
    .ft-cta-strip { padding: 36px 24px; }
    .ft-brand-box { padding: 32px 24px; }
    .ft-brand-title { font-size: 1.3rem; }
    .ft-brand-top { flex-direction: column; gap: 14px; }
  }
`;

/* ─── data ───────────────────────────────────────────────────────────────── */
const ALL_FEATURES = [
  {
    icon: '🔗',
    title: 'URL Shortening',
    desc: 'Instantly convert any long URL into a compact, shareable short link. Works for any publicly accessible URL — web pages, files, videos, and more.',
    plans: ['free', 'pro', 'business'],
    category: 'Core',
  },
  {
    icon: '📊',
    title: 'Click Tracking',
    desc: 'See how many times each of your links has been clicked. Real-time counters update automatically so you always have up-to-date performance data.',
    plans: ['free', 'pro', 'business'],
    category: 'Core',
  },
  {
    icon: '🔳',
    title: 'QR Code (free for a limited time)',
    desc: 'Every Pro link gets an on-demand QR code. Download a high-resolution PNG perfect for print, digital signage, business cards, or event materials.',
    plans: ['free', 'pro', 'business'],
    category: 'Core',
  },
  {
    icon: '⚡',
    title: 'Fast Global Redirects',
    desc: 'Every redirect is handled in milliseconds. Our infrastructure is optimised for low-latency performance so your audience gets where they are going instantly.',
    plans: ['free', 'pro', 'business'],
    category: 'Platform',
  },
  {
    icon: '🛡️',
    title: 'Secure & Reliable',
    desc: 'All data is stored securely with bcrypt-hashed passwords and JWT authentication. Links and analytics are backed by a PostgreSQL database with daily backups.',
    plans: ['free', 'pro', 'business'],
    category: 'Platform',
  },
  {
    icon: '🎯',
    title: 'Custom Short Aliases',
    desc: 'Choose a memorable, branded short code for your links (e.g. rdrto.com/my-promo). Custom aliases make links easy to share verbally or in print.',
    plans: ['pro', 'business'],
    category: 'Links',
  },
  {
    icon: '🔒',
    title: 'Password Protection',
    desc: 'Add a password gate to any link. Visitors are prompted to enter the correct password before being redirected — great for private or restricted content.',
    plans: ['pro', 'business'],
    category: 'Links',
  },
  {
    icon: '⏰',
    title: 'Link Expiration Dates',
    desc: 'Set an automatic expiry date on any link. Once the date passes, the link stops working. Perfect for time-limited offers, event registration, and promotions.',
    plans: ['pro', 'business'],
    category: 'Links',
  },
  {
    icon: '✏️',
    title: 'Edit Existing Links',
    desc: 'Changed your mind? Update the destination URL, expiration date, or password of any link you have already created — no need to delete and recreate.',
    plans: ['pro', 'business'],
    category: 'Links',
  },
  {
    icon: '📈',
    title: 'Advanced Analytics Dashboard',
    desc: 'Explore rich charts and tables for every link: daily click trends, geographic breakdown, date clicks, hour of day, device types, browsers, and top referrers — all in one place.',
    plans: ['pro', 'business'],
    category: 'Analytics',
  },
  /*   {
      icon: '🌍',
      title: 'Geographic Insights',
      desc: 'Discover which countries and regions your audience is clicking from. A geographic heatmap gives you an instant visual overview of your global reach.',
      plans: ['pro', 'business'],
      category: 'Analytics',
    }, */
  {
    icon: '📱',
    title: 'Device & Browser Breakdown',
    desc: 'Understand how your audience accesses your links. See splits across mobile, tablet, and desktop as well as the most popular browsers and operating systems.',
    plans: ['pro', 'business'],
    category: 'Analytics',
  },
  {
    icon: '🔁',
    title: 'Referrer Tracking',
    desc: 'Know exactly where your traffic is coming from. Track referrer URLs and identify which website, social post, or email campaign is driving the most clicks.',
    plans: ['pro', 'business'],
    category: 'Analytics',
  },
  /*   {
      icon: '👥',
      title: 'Team Workspaces',
      desc: 'Collaborate with your team under one shared account. Manage links, view analytics, and assign access levels — all in a single Business workspace.',
      plans: ['business'],
      category: 'Team',
    }, */
  /*   {
      icon: '📦',
      title: 'Bulk Operations',
      desc: 'Create, update, or delete multiple links in one go. Ideal for marketing agencies and power users managing large link inventories across many campaigns.',
      plans: ['business'],
      category: 'Team',
    }, */
  /*     {
          icon: '↪️',
          title: 'Advanced Redirects',
          desc: 'Go beyond a simple redirect. Route visitors to different destinations based on their device type, location, or time of day for hyper-targeted experiences.',
          plans: ['business'],
          category: 'Team',
      }, */
  {
    icon: '🏷️',
    title: 'Brand Name URLs',
    desc: 'Embed your brand name directly into every short URL. Choose Brand Path (rdrto.com/yourbrand/code) or Brand Subdomain (yourbrand.rdrto.com/code) — both work out of the box with no DNS setup required.',
    plans: ['pro', 'business'],
    category: 'Team',
  },
  {
    icon: '🏥',
    title: 'Self-Healing Links',
    desc: 'Never lose traffic to a broken link again. Our engine detects if your primary URL goes down and instantly redirects visitors to a healthy backup URL of your choice and alerting you.',
    plans: ['pro', 'business'],
    category: 'Analytics',
  },
  {
    icon: '🚥',
    title: 'Link Intelligence Score',
    desc: 'Get an instant 0–100 health score for your destination URLs. We automatically grade links based on reachability, speed, SSL, and safety flags to ensure an optimal experience for your audience.',
    plans: ['business'],
    category: 'Analytics',
  },
];

const PLAN_FILTERS = ['All', 'Free', 'Pro', 'Business'];

const TABLE_ROWS = [
  { cat: 'Links & Core', feature: 'Max links per account', free: '20', pro: '1,000', biz: '10,000' },
  { cat: 'Links & Core', feature: 'Basic URL shortening', free: true, pro: true, biz: true },
  { cat: 'Links & Core', feature: 'QR code generation', free: true, pro: true, biz: true },
  { cat: 'Links & Core', feature: 'Custom short aliases', free: false, pro: true, biz: true },
  { cat: 'Links & Core', feature: 'Password protection', free: false, pro: true, biz: true },
  { cat: 'Links & Core', feature: 'Link expiration dates', free: false, pro: true, biz: true },
  { cat: 'Links & Core', feature: 'Edit existing links', free: false, pro: true, biz: true },
  { cat: 'Analytics', feature: 'Basic click tracking', free: true, pro: true, biz: true },
  { cat: 'Analytics', feature: 'Advanced Analytics dashboard', free: false, pro: true, biz: true },
  { cat: 'Analytics', feature: 'Geographic insights', free: false, pro: true, biz: true },
  { cat: 'Analytics', feature: 'Device & browser data', free: false, pro: true, biz: true },
  { cat: 'Analytics', feature: 'Referrer tracking', free: false, pro: true, biz: true },
  { cat: 'Analytics', feature: 'Link intelligence score', free: false, pro: false, biz: true },
  { cat: 'Analytics', feature: 'Self-healing links (failover)', free: false, pro: true, biz: true },
  /*   { cat: 'Team', feature: 'Team workspaces', free: false, pro: false, biz: true },
    { cat: 'Team', feature: 'Bulk link operations', free: false, pro: false, biz: true },
    { cat: 'Team', feature: 'Advanced redirects', free: false, pro: false, biz: true }, */
  { cat: 'Team', feature: 'Brand Name URLs (path & subdomain)', free: false, pro: true, biz: true },
  { cat: 'Support', feature: 'Community support', free: true, pro: true, biz: true },
  { cat: 'Support', feature: 'Priority email support', free: false, pro: true, biz: true },
  { cat: 'Support', feature: 'Dedicated account manager', free: false, pro: false, biz: true },
  { cat: 'Pricing', feature: 'Monthly price', free: '$0', pro: '$19', biz: '$49' },
];

/* ─── helpers ────────────────────────────────────────────────────────────── */
const Cell = ({ val }) => {
  if (val === true) return <span className="ft-check">✓</span>;
  if (val === false) return <span className="ft-cross">✗</span>;
  return <span className="ft-val">{val}</span>;
};

/* ─── component ──────────────────────────────────────────────────────────── */
const Features = () => {
  const [activeTab, setActiveTab] = useState('All');

  const visibleFeatures = activeTab === 'All'
    ? ALL_FEATURES
    : ALL_FEATURES.filter(f => f.plans.includes(activeTab.toLowerCase()));

  // Build table rows grouped by category
  let lastCat = null;
  const tableRows = [];
  TABLE_ROWS.forEach((row, i) => {
    if (row.cat !== lastCat) { tableRows.push({ isCat: true, cat: row.cat, key: `cat-${i}` }); lastCat = row.cat; }
    tableRows.push({ ...row, key: `row-${i}` });
  });

  return (
    <div className="ft-page">
      <style>{STYLES}</style>

      {/* ── Hero ── */}
      <section className="ft-hero">
        <div className="ft-hero-bg">
          <div className="ft-hero-blob ft-hero-blob-1" />
          <div className="ft-hero-blob ft-hero-blob-2" />
        </div>
        <div className="ft-hero-inner">
          <div className="ft-eyebrow">✦ Platform Features</div>
          <h1 className="ft-h1">Everything you need to<br /><span>manage links smarter</span></h1>
          <p className="ft-hero-sub">
            From instant URL shortening to deep analytics and team collaboration,
            RDRTO gives you the tools to share, track, and optimise every link you create.
          </p>
          <Link to="/signup" className="ft-hero-cta">🚀 Get Started Free</Link>
        </div>
      </section>

      {/* ── Feature cards ── */}
      <div className="ft-section">
        <h2 className="ft-section-title">All Features</h2>
        <p className="ft-section-sub">Filter by plan to see what's available at each tier.</p>

        {/* Plan filter tabs */}
        <div className="ft-tabs">
          {PLAN_FILTERS.map(tab => (
            <button
              key={tab}
              className={`ft-tab${activeTab === tab ? ' ft-tab-active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="ft-cards">
          {visibleFeatures.map((f, i) => (
            <div className="ft-card" key={i}>
              <div className="ft-card-icon-wrap">{f.icon}</div>
              <div className="ft-card-header">
                <h3 className="ft-card-title">{f.title}</h3>
              </div>
              <p className="ft-card-desc">{f.desc}</p>
              <div className="ft-plan-pills">
                {f.plans.includes('free') && <span className="ft-pill ft-pill-free">Free</span>}
                {f.plans.includes('pro') && <span className="ft-pill ft-pill-pro">Pro</span>}
                {f.plans.includes('business') && <span className="ft-pill ft-pill-business">Business</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Brand Name URL Showcase ── */}
      <div className="ft-brand-showcase">
        <div className="ft-brand-box">
          <div className="ft-brand-top">
            <div className="ft-brand-icon">🏷️</div>
            <div>
              <div className="ft-brand-badge">🏢 Pro & Business Exclusive</div>
              <h2 className="ft-brand-title">Brand Name URLs</h2>
              <p className="ft-brand-desc">
                Stop sharing generic <code style={{ color: '#a5b4fc', background: 'rgba(99,102,241,0.1)', padding: '2px 6px', borderRadius: 5 }}>rdrto.com/abc123</code> links.
                With a Business plan you can embed your brand name directly into every short URL —
                so recipients immediately know who the link is from, before they even click it.
                Choose between two flexible URL structures to match your branding strategy.
              </p>
            </div>
          </div>

          {/* Two mode cards */}
          <div className="ft-brand-modes">

            {/* Mode 1 — Brand Path */}
            <div className="ft-brand-mode">
              <p className="ft-brand-mode-label">Mode 1</p>
              <p className="ft-brand-mode-name">Brand Path</p>
              <p className="ft-brand-mode-desc">
                Your brand name appears as a folder in the URL path.
                All your links share the same base domain and your name acts as a namespace —
                easy to set up and works without any DNS changes.
              </p>
              <div className="ft-brand-url-demo">
                <div><span className="ft-url-dim">rdrto.com/</span><span className="ft-url-brand">acme</span><span className="ft-url-dim">/</span><span className="ft-url-code">summer-sale</span></div>
                <div><span className="ft-url-dim">rdrto.com/</span><span className="ft-url-brand">acme</span><span className="ft-url-dim">/</span><span className="ft-url-code">product-launch</span></div>
                <div><span className="ft-url-dim">rdrto.com/</span><span className="ft-url-brand">acme</span><span className="ft-url-dim">/</span><span className="ft-url-code">newsletter-q1</span></div>
              </div>
            </div>

            {/* Mode 2 — Brand Subdomain */}
            <div className="ft-brand-mode">
              <p className="ft-brand-mode-label">Mode 2</p>
              <p className="ft-brand-mode-name">Brand Subdomain</p>
              <p className="ft-brand-mode-desc">
                Your brand name becomes its own subdomain, giving every link a fully
                branded look. Ideal for agencies and companies that want their name
                front-and-centre rather than grouped under rdrto.com.
              </p>
              <div className="ft-brand-url-demo">
                <div><span className="ft-url-brand">acme</span><span className="ft-url-dim">.rdrto.com/</span><span className="ft-url-code">summer-sale</span></div>
                <div><span className="ft-url-brand">acme</span><span className="ft-url-dim">.rdrto.com/</span><span className="ft-url-code">product-launch</span></div>
                <div><span className="ft-url-brand">acme</span><span className="ft-url-dim">.rdrto.com/</span><span className="ft-url-code">newsletter-q1</span></div>
              </div>
            </div>

          </div>

          {/* How to set up steps */}
          <div className="ft-brand-steps">
            <p className="ft-brand-steps-title">⚙️ How it works — 3 steps</p>
            <ul className="ft-brand-steps-list">
              <li className="ft-brand-step">
                <div className="ft-brand-step-num">1</div>
                <p className="ft-brand-step-text">
                  <strong>Set your Brand Name</strong> — Go to Brand Settings in your dashboard and enter a short, slug-friendly name (e.g. <code style={{ color: '#fbbf24' }}>acme</code>). This is used in every branded URL you create.
                </p>
              </li>
              <li className="ft-brand-step">
                <div className="ft-brand-step-num">2</div>
                <p className="ft-brand-step-text">
                  <strong>Choose URL type when creating a link</strong> — When you create or edit any link, select <em>Brand Path</em> or <em>Brand Subdomain</em> from the URL Type dropdown. The preview will show you the exact URL your audience will see.
                </p>
              </li>
              <li className="ft-brand-step">
                <div className="ft-brand-step-num">3</div>
                <p className="ft-brand-step-text">
                  <strong>Share with confidence</strong> — Your branded short URL is live instantly. All analytics, click counts, QR codes, and redirect rules work exactly the same as standard links.
                </p>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* ── Link Intelligence Score Showcase ── */}
      <div className="ft-brand-showcase" style={{ paddingTop: 0 }}>
        <div className="ft-brand-box" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(16,185,129,0.04) 100%)', borderColor: 'rgba(34,197,94,0.22)' }}>
          <div className="ft-brand-top">
            <div className="ft-brand-icon" style={{ background: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.28)' }}>🚥</div>
            <div>
              <div className="ft-brand-badge" style={{ color: '#4ade80', background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.22)' }}>🏢 Business Exclusive</div>
              <h2 className="ft-brand-title">Link Intelligence Score</h2>
              <p className="ft-brand-desc">
                Stop guessing whether your destination URLs are working — know instantly.
                Our Link Intelligence engine continuously grades your links from 0 to 100 based on six crucial real-world signals.
                Ensure your audience always enjoys a fast, safe, and reliable experience.
              </p>
            </div>
          </div>

          <div className="ft-brand-modes" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* Signal 1: Performance */}
            <div className="ft-brand-mode" style={{ borderColor: 'rgba(34,197,94,0.15)' }}>
              <p className="ft-brand-mode-label" style={{ color: '#4ade80' }}>Signal 1</p>
              <p className="ft-brand-mode-name">Performance & Reachability</p>
              <p className="ft-brand-mode-desc">
                We continuously ping your destination URL to check if it's online, how fast it responds (Time To First Byte), and whether connection timeouts could hurt conversions.
              </p>
            </div>

            {/* Signal 2: Security */}
            <div className="ft-brand-mode" style={{ borderColor: 'rgba(34,197,94,0.15)' }}>
              <p className="ft-brand-mode-label" style={{ color: '#4ade80' }}>Signal 2</p>
              <p className="ft-brand-mode-name">Security & Trust</p>
              <p className="ft-brand-mode-desc">
                Every link is checked against global safe browsing lists to prevent malware or phishing distribution. We also verify proper HTTPS/SSL configuration to ensure trust.
              </p>
            </div>

            {/* Signal 3: Engagement */}
            <div className="ft-brand-mode" style={{ borderColor: 'rgba(34,197,94,0.15)' }}>
              <p className="ft-brand-mode-label" style={{ color: '#4ade80' }}>Signal 3</p>
              <p className="ft-brand-mode-name">Engagement & Freshness</p>
              <p className="ft-brand-mode-desc">
                A link with no clicks isn't as valuable as one actively driving traffic. We factor in link age and recent click activity to highlight your most relevant, active URLs.
              </p>
            </div>
          </div>

          {/* How to use the score */}
          <div className="ft-brand-steps" style={{ borderColor: 'rgba(34,197,94,0.15)' }}>
            <p className="ft-brand-steps-title" style={{ color: '#4ade80' }}>⚙️ How to use the intelligence score</p>
            <ul className="ft-brand-steps-list">
              <li className="ft-brand-step">
                <div className="ft-brand-step-num" style={{ background: 'rgba(34,197,94,0.15)', borderColor: 'rgba(34,197,94,0.25)', color: '#4ade80' }}>1</div>
                <p className="ft-brand-step-text">
                  <strong>Spot broken links instantly</strong> — Instead of manually checking hundreds of links, sort your dashboard by Intelligence Score to immediately flag offline or slow pages before your users find them.
                </p>
              </li>
              <li className="ft-brand-step">
                <div className="ft-brand-step-num" style={{ background: 'rgba(34,197,94,0.15)', borderColor: 'rgba(34,197,94,0.25)', color: '#4ade80' }}>2</div>
                <p className="ft-brand-step-text">
                  <strong>View the breakdown</strong> — Click on any Link Intelligence Score badge in your Analytics page to see exactly why a link lost points, allowing you to fix the underlying issue.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Self-Healing Links Showcase ── */}
      <div className="ft-brand-showcase" style={{ paddingTop: 0 }}>
        <div className="ft-brand-box" style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.06) 0%, rgba(14,165,233,0.04) 100%)', borderColor: 'rgba(56,189,248,0.22)' }}>
          <div className="ft-brand-top">
            <div className="ft-brand-icon" style={{ background: 'rgba(56,189,248,0.12)', borderColor: 'rgba(56,189,248,0.28)' }}>🏥</div>
            <div>
              <div className="ft-brand-badge" style={{ color: '#38bdf8', background: 'rgba(56,189,248,0.1)', borderColor: 'rgba(56,189,248,0.22)' }}>🏢 Business Exclusive</div>
              <h2 className="ft-brand-title">Self-Healing Links</h2>
              <p className="ft-brand-desc">
                Protect your brand reputation and save lost conversions. If your primary destination server crashes or throws a 404, our engine detects the failure in real-time and automatically routes visitors to a pre-defined backup URL while alerting you.
              </p>
            </div>
          </div>

          <div className="ft-brand-modes" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div className="ft-brand-mode" style={{ borderColor: 'rgba(56,189,248,0.15)' }}>
              <p className="ft-brand-mode-label" style={{ color: '#38bdf8' }}>Feature 1</p>
              <p className="ft-brand-mode-name">Zero-Downtime Failover</p>
              <p className="ft-brand-mode-desc">
                Set an emergency backup URL (like your homepage or social profile). If your primary link throws a 404 or times out, visitors are instantly redirected to the backup.
              </p>
            </div>

            <div className="ft-brand-mode" style={{ borderColor: 'rgba(56,189,248,0.15)' }}>
              <p className="ft-brand-mode-label" style={{ color: '#38bdf8' }}>Feature 2</p>
              <p className="ft-brand-mode-name">Instant Email Alerts</p>
              <p className="ft-brand-mode-desc">
                The moment a failover occurs, you receive an immediate alert email with the exact HTTP error code and details, so you can fix your primary server fast.
              </p>
            </div>

            <div className="ft-brand-mode" style={{ borderColor: 'rgba(56,189,248,0.15)' }}>
              <p className="ft-brand-mode-label" style={{ color: '#38bdf8' }}>Feature 3</p>
              <p className="ft-brand-mode-name">Automatic Recovery</p>
              <p className="ft-brand-mode-desc">
                Our worker continuously polls your broken primary URL in the background. The moment it comes back online, primary routing is automatically restored for incoming clicks.
              </p>
            </div>
          </div>

          {/* How it works steps */}
          <div className="ft-brand-steps" style={{ borderColor: 'rgba(56,189,248,0.15)', marginTop: 32 }}>
            <p className="ft-brand-steps-title" style={{ color: '#38bdf8' }}>⚙️ How it works</p>
            <ul className="ft-brand-steps-list">
              <li className="ft-brand-step">
                <div className="ft-brand-step-num" style={{ background: 'rgba(56,189,248,0.15)', borderColor: 'rgba(56,189,248,0.25)', color: '#38bdf8' }}>1</div>
                <p className="ft-brand-step-text">
                  <strong>Enroll your link</strong> — Enable Self-Healing on any link from your dashboard. Our engine will immediately start monitoring its health every 1 to 5 minutes based on traffic volume.
                </p>
              </li>
              <li className="ft-brand-step">
                <div className="ft-brand-step-num" style={{ background: 'rgba(56,189,248,0.15)', borderColor: 'rgba(56,189,248,0.25)', color: '#38bdf8' }}>2</div>
                <p className="ft-brand-step-text">
                  <strong>Save your emergency config</strong> — On the link Analytics page, enter a Backup URL and an Alert Email address. You can also view the full log of historical healing events and forced health checks.
                </p>
              </li>
              <li className="ft-brand-step">
                <div className="ft-brand-step-num" style={{ background: 'rgba(56,189,248,0.15)', borderColor: 'rgba(56,189,248,0.25)', color: '#38bdf8' }}>3</div>
                <p className="ft-brand-step-text">
                  <strong>Never worry about dead links</strong> — Let RDRTO do the monitoring. You'll simply receive an automated email the next time your primary site goes offline while your visitors go to safety.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="ft-section" style={{ paddingBottom: 0 }}>
        <h2 className="ft-section-title">Plan Comparison</h2>
        <p className="ft-section-sub">A full side-by-side view of every feature across all plans.</p>

        <div className="ft-table-wrap">
          <table className="ft-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th className="ft-col-pro">Pro</th>
                <th className="ft-col-business">Business</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map(row =>
                row.isCat ? (
                  <tr key={row.key} className="ft-cat-row">
                    <td colSpan={4}>{row.cat}</td>
                  </tr>
                ) : (
                  <tr key={row.key}>
                    <td>{row.feature}</td>
                    <td><Cell val={row.free} /></td>
                    <td><Cell val={row.pro} /></td>
                    <td><Cell val={row.biz} /></td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── CTA strip ── */}
      <div style={{ padding: '80px 24px 0' }}>
        <div className="ft-cta-strip">
          <h2 className="ft-cta-title">Ready to take your links further?</h2>
          <p className="ft-cta-sub">Start free — no credit card required. Upgrade any time as your needs grow.</p>
          <div className="ft-cta-btns">
            <Link to="/signup" className="ft-cta-primary">🚀 Create Free Account</Link>
            <Link to="/upgrade" className="ft-cta-secondary">View Pricing →</Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Features;
